import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { homePathForUser, persistUserSession } from '../utils/sessionUser';
import { notifySessionUpdated } from '../brandKits';

const BRAND = 'var(--brand-primary, #10575c)';
const SESSION_KEY = 'ipm_demo_mode';
// Durable (localStorage) snapshot of the real user taken when demo mode starts.
// Demo session state lives in sessionStorage (cleared on browser close), but the
// impersonated user is written to localStorage.user (which survives a restart).
// Without a durable copy of the admin, closing the browser mid-demo would leave
// you stuck as the demo account on reopen. reconcileDemoSession() rolls it back.
const PREV_USER_KEY = 'ipm_demo_prev_user';

const ROLE_ORDER = [
    'agency', 'agency_agent', 'independent_agent', 'investor', 'buyer', 'seller', 'tenant',
    'enterprise',
    'partner_enterprise',
    'partner_conveyancer', 'partner_conveyancer_agent', 'partner_bond', 'partner_agent',
    'partner_bank', 'partner_short_term_insurance', 'partner_life_insurance', 'partner_valuer',
];

const ROLE_LABELS = {
    agency: 'Agency',
    agency_agent: 'Agent',
    independent_agent: 'Independent Agent',
    investor: 'Investor / Buyer',
    buyer: 'Buyer',
    seller: 'Seller',
    tenant: 'Tenant',
    enterprise: 'Property Enterprise',
    partner_enterprise: 'Mzansi Structures (Bond HQ)',
    partner_conveyancer: 'Conveyancer',
    partner_conveyancer_agent: 'Conveyancing Attorney',
    partner_bond: 'Bond Franchise',
    partner_agent: 'Bond Consultant',
    partner_bank: 'Bank',
    partner_short_term_insurance: 'Short-term Insurance',
    partner_life_insurance: 'Life Insurance',
    partner_valuer: 'Property Valuer',
};

const ROLE_HOME_ROUTE = {
    agency: '/dashboard',
    agency_agent: '/dashboard',
    independent_agent: '/dashboard',
    investor: '/investor-portfolio',
    buyer: '/investor-portfolio',
    seller: '/dashboard',
    tenant: '/tenant-portal',
    enterprise: '/dashboard',
    partner_enterprise: '/partner-enterprise-dashboard',
    partner_conveyancer: '/conveyancer',
    partner_conveyancer_agent: '/conveyancer',
    partner_bond: '/bond-originator',
    partner_agent: '/bond-originator',
    // The remaining v1 partner types share the dynamic dashboard.
    partner_bank: '/partner-dashboard',
    partner_short_term_insurance: '/partner-dashboard',
    partner_life_insurance: '/partner-dashboard',
    partner_valuer: '/partner-dashboard',
};

export function getDemoState() {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (_) {
        return null;
    }
}

function buildFakeUserFromDemo(demoUser, adminToken) {
    // Prefer the short-lived demo JWT minted server-side (issued by /api/admin/demo-users).
    // Falls back to the admin's token only if the backend didn't supply one, so API calls
    // are authenticated as the demo user and the dashboard loads that user's real data.
    const {
        _options,
        _label,
        _propertyCount,
        _pinned,
        _demoToken,
        ...rest
    } = demoUser || {};
    const token = _demoToken || adminToken;
    return {
        ...rest,
        token,
        _id: rest._id,
        // Marks localStorage.user as an admin-impersonated demo account so we can
        // detect (and roll back) a stale demo user left behind after the browser
        // is closed mid-demo. See reconcileDemoSession().
        _demoImpersonation: true,
        mustChangePassword: false,
    };
}

function writeImpersonatedUser(fakeUser) {
    persistUserSession(fakeUser);
}

/**
 * Startup guard. If the browser was closed mid-demo, sessionStorage (the demo
 * session) is gone but localStorage.user may still be the impersonated demo
 * account - which would drop you straight into that account on reopen. Detect
 * that and restore the real (admin) user, or sign out if it can't be recovered.
 * Safe to call on every app boot; it no-ops during an active demo session.
 */
export function reconcileDemoSession() {
    try {
        if (getDemoState()) return; // demo session still live → nothing to do
        let user = null;
        try { user = JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { user = null; }
        if (user && user._demoImpersonation) {
            let prev = null;
            try { prev = JSON.parse(localStorage.getItem(PREV_USER_KEY) || 'null'); } catch (_) { prev = null; }
            if (prev) persistUserSession(prev);
            else localStorage.removeItem('user');
        }
        localStorage.removeItem(PREV_USER_KEY);
    } catch (_) { /* noop */ }
}

export function startDemoMode(realUser, demoUsers, selectedRole, demoEnvId = null) {
    const availableRoles = ROLE_ORDER.filter((k) => demoUsers[k]);
    const demoUser = demoUsers[selectedRole];
    if (!demoUser) return false;

    sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        realUser,
        demoUsers,
        selectedRole,
        availableRoles,
        adminToken: realUser?.token || null,
        // When set, the roster is scoped to a DemoEnvironment; refresh paths must
        // re-pull from that env (not the curated /demo-users roster) so switching
        // accounts / re-minting tokens never clobbers env accounts with custom ones.
        demoEnvId: demoEnvId || null,
    }));

    // Durable copy of the real user so we can recover it even if the browser is
    // closed mid-demo (sessionStorage - and thus realUser above - would be lost).
    try { localStorage.setItem(PREV_USER_KEY, JSON.stringify(realUser)); } catch (_) { /* noop */ }

    writeImpersonatedUser(buildFakeUserFromDemo(demoUser, realUser.token));
    return true;
}

export function switchDemoRole(roleKey) {
    const state = getDemoState();
    if (!state || !state.demoUsers[roleKey]) return null;

    state.selectedRole = roleKey;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));

    const demoUser = state.demoUsers[roleKey];
    const fakeUser = buildFakeUserFromDemo(demoUser, state.realUser?.token);
    writeImpersonatedUser(fakeUser);
    return homePathForUser(fakeUser) || ROLE_HOME_ROUTE[roleKey] || '/dashboard';
}

// For multi-option roles (e.g. Agency = Marder | Foros | Tyson), swap the
// active account *within* the current role without changing the role itself.
// We mutate demoUsers[roleKey] in place - preserving _options + _label so the
// dropdown keeps showing all choices - but copy in the selected option's
// identity, agencyName, demo token, etc.
export function switchDemoAccount(roleKey, accountId) {
    const state = getDemoState();
    if (!state) return null;
    const slot = state.demoUsers?.[roleKey];
    if (!slot || !Array.isArray(slot._options)) return null;
    const option = slot._options.find((o) => String(o._id) === String(accountId));
    if (!option) return null;

    const { _options, _label } = slot;
    state.demoUsers[roleKey] = { ...option, _options, _label };
    state.selectedRole = roleKey;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));

    const fakeUser = buildFakeUserFromDemo(state.demoUsers[roleKey], state.realUser?.token);
    writeImpersonatedUser(fakeUser);
    return homePathForUser(fakeUser) || ROLE_HOME_ROUTE[roleKey] || '/dashboard';
}

// Patch the cached demo snapshot for a given user id with a partial update
// (e.g. { photo, logo, name, bio }). Without this, anything edited via Settings
// while in demo mode (a new agency logo, an updated bio, …) gets clobbered the
// next time you switch demo accounts and switch back, because switchDemoRole /
// switchDemoAccount overwrite localStorage.user with this cached snapshot.
//
// Walks every role slot and every `_options` entry - a single user can show up
// in more than one place (e.g. the active slot and the options list).
//
// Returns true if at least one slot was patched.
export function updateDemoUserSnapshot(userId, patch) {
    if (!userId || !patch || typeof patch !== 'object') return false;
    const state = getDemoState();
    if (!state || !state.demoUsers) return false;
    const target = String(userId);
    let changed = false;

    for (const roleKey of Object.keys(state.demoUsers)) {
        const slot = state.demoUsers[roleKey];
        if (!slot) continue;
        if (String(slot._id) === target) {
            state.demoUsers[roleKey] = { ...slot, ...patch };
            changed = true;
        }
        if (Array.isArray(slot._options)) {
            const nextOptions = slot._options.map((opt) =>
                String(opt._id) === target ? { ...opt, ...patch } : opt
            );
            if (nextOptions.some((opt, i) => opt !== slot._options[i])) {
                state.demoUsers[roleKey] = {
                    ...(state.demoUsers[roleKey] || slot),
                    _options: nextOptions,
                };
                changed = true;
            }
        }
    }

    if (changed) sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
    return changed;
}

export function exitDemoMode() {
    const state = getDemoState();
    if (state?.realUser) persistUserSession(state.realUser);
    sessionStorage.removeItem(SESSION_KEY);
    try { localStorage.removeItem(PREV_USER_KEY); } catch (_) { /* noop */ }
    notifySessionUpdated();
}

/** Drop demo session without restoring the admin user (e.g. forced logout after 401). */
export function clearDemoSession() {
    sessionStorage.removeItem(SESSION_KEY);
    try { localStorage.removeItem(PREV_USER_KEY); } catch (_) { /* noop */ }
}

/** Parse demo login email from review hint text, e.g. "Agency demo: tyson.sandton@demo.com / Phoenix@1". */
export function parseDemoEmailFromHint(hint) {
    if (!hint) return null;
    const match = String(hint).match(/[\w.+-]+@[\w.-]+\.\w+/i);
    return match ? match[0].toLowerCase() : null;
}

/** Find a demo roster entry by email across all roles and account options. */
export function findDemoAccountByEmail(demoUsers, email) {
    const target = String(email || '').trim().toLowerCase();
    if (!target || !demoUsers) return null;
    for (const roleKey of ROLE_ORDER) {
        const slot = demoUsers[roleKey];
        if (!slot?._id) continue;
        const options = Array.isArray(slot._options) && slot._options.length > 0
            ? slot._options
            : [slot];
        for (const opt of options) {
            if (String(opt.email || '').toLowerCase() === target) {
                return { roleKey, userId: String(opt._id) };
            }
        }
    }
    return null;
}

/**
 * Enter demo mode (or switch account) as the given demo email.
 * Returns the role home route on success, null if email not in roster.
 */
export function enterDemoAsEmail(realUser, demoUsers, email) {
    const match = findDemoAccountByEmail(demoUsers, email);
    if (!match || !realUser) return null;

    const { roleKey, userId } = match;
    const existing = getDemoState();

    if (!existing) {
        if (!startDemoMode(realUser, demoUsers, roleKey)) return null;
    } else {
        refreshDemoAccountOptions(demoUsers);
        const state = getDemoState();
        if (state?.selectedRole !== roleKey) {
            switchDemoRole(roleKey);
        }
    }

    const after = getDemoState();
    const activeId = String(after?.demoUsers?.[roleKey]?._id || '');
    if (activeId !== userId) {
        switchDemoAccount(roleKey, userId);
    }

    return homePathForUser(after?.demoUsers?.[roleKey]) || ROLE_HOME_ROUTE[roleKey] || '/dashboard';
}

/** Dev admin check - works while viewing as a demo account (uses saved real admin user). */
export function isDevAdminViewer() {
    try {
        const demo = getDemoState();
        if (demo?.realUser) {
            const role = String(demo.realUser.role || '').toLowerCase();
            return role === 'admin' && String(demo.realUser.adminTier || '').toLowerCase() === 'dev';
        }
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        if (!user) return false;
        const role = String(user.role || '').toLowerCase();
        return role === 'admin' && String(user.adminTier || '').toLowerCase() === 'dev';
    } catch {
        return false;
    }
}

/** JWT for bug-review API calls - admin token when browsing inside demo mode. */
export function getBugReviewAdminToken() {
    const demo = getDemoState();
    if (demo?.adminToken) return demo.adminToken;
    if (demo?.realUser?.token) return demo.realUser.token;
    try {
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        return user?.token || null;
    } catch {
        return null;
    }
}

function optionIds(slot) {
    const opts = Array.isArray(slot?._options) ? slot._options : slot ? [slot] : [];
    return opts.map((o) => String(o._id)).sort().join(',');
}

function mergeDemoTokensFromFresh(slot, freshSlot) {
    if (!slot || !freshSlot) return slot;
    const freshOpts = Array.isArray(freshSlot._options) && freshSlot._options.length > 0
        ? freshSlot._options
        : null;
    if (freshOpts) {
        const byId = new Map(freshOpts.map((o) => [String(o._id), o]));
        const selectedId = String(slot._id || '');
        const chosen = byId.get(selectedId) || freshOpts[0];
        const mergedOptions = (Array.isArray(slot._options) ? slot._options : freshOpts).map((opt) => {
            const fresh = byId.get(String(opt._id));
            return fresh?._demoToken ? { ...opt, _demoToken: fresh._demoToken } : opt;
        });
        return {
            ...slot,
            ...chosen,
            _options: mergedOptions,
            _label: freshSlot._label || slot._label,
            _demoToken: chosen._demoToken || slot._demoToken,
        };
    }
    return freshSlot._demoToken ? { ...slot, _demoToken: freshSlot._demoToken } : slot;
}

/** Re-mint demo JWTs from the server roster (8h TTL) without changing selected accounts. */
export function refreshDemoTokensFromRoster(freshDemoUsers) {
    const state = getDemoState();
    if (!state?.demoUsers || !freshDemoUsers) return false;

    let changed = false;
    for (const roleKey of ROLE_ORDER) {
        const fresh = freshDemoUsers[roleKey];
        if (!fresh?._id || !state.demoUsers[roleKey]) continue;
        const merged = mergeDemoTokensFromFresh(state.demoUsers[roleKey], fresh);
        if (!merged._demoToken) continue;
        state.demoUsers[roleKey] = merged;
        changed = true;
    }

    if (!changed) return false;

    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
    const active = state.demoUsers[state.selectedRole];
    if (active) {
        writeImpersonatedUser(buildFakeUserFromDemo(active, state.realUser?.token));
    }
    return true;
}

/** Fetch a fresh demo roster and update the active session token (used after 401 in demo mode). */
export async function ensureFreshDemoToken() {
    const state = getDemoState();
    const adminToken = getAdminToken(state);
    if (!state || !adminToken) return false;
    try {
        const fresh = await fetchDemoRoster(adminToken, state.demoEnvId);
        refreshDemoTokensFromRoster(fresh);
        refreshDemoAccountOptions(fresh);
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        return !!(user && user.token);
    } catch {
        return false;
    }
}

// Merge fresh demo roster into an active session - updates account lists AND
// adds roles that were missing when demo mode was first launched (e.g. Mzansi HQ).
export function refreshDemoAccountOptions(freshDemoUsers) {
    const state = getDemoState();
    if (!state?.demoUsers || !freshDemoUsers) return false;

    let changed = false;

    for (const roleKey of ROLE_ORDER) {
        const fresh = freshDemoUsers[roleKey];
        if (!fresh?._id) continue;

        const freshOpts = Array.isArray(fresh._options) && fresh._options.length > 0
            ? fresh._options
            : null;
        const freshKey = freshOpts ? optionIds({ _options: freshOpts }) : String(fresh._id);

        const current = state.demoUsers[roleKey];
        const currentKey = current
            ? (Array.isArray(current._options) && current._options.length > 0
                ? optionIds(current)
                : String(current._id))
            : null;

        if (currentKey === freshKey) continue;

        if (freshOpts) {
            const selectedId = String(current?._id || '');
            let opts = freshOpts.filter((o) => !o.mergedIntoAgencyId);
            const keepStale = selectedId && current?._id && !current.mergedIntoAgencyId
                && !opts.some((o) => String(o._id) === selectedId);
            if (keepStale) {
                const { _options: _drop, ...rest } = current;
                opts = [rest, ...opts];
            }
            const chosen = opts.find((o) => String(o._id) === selectedId)
                || opts.find((o) => /bryanston/i.test(`${o.agencyName || ''} ${o.email || ''}`))
                || opts[0];
            if (!chosen) continue;
            state.demoUsers[roleKey] = { ...chosen, _options: opts, _label: fresh._label };
        } else {
            state.demoUsers[roleKey] = { ...fresh, _label: fresh._label };
        }
        changed = true;
    }

    if (!changed) return false;

    state.availableRoles = ROLE_ORDER.filter((k) => state.demoUsers[k]);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));

    const active = state.demoUsers[state.selectedRole];
    if (active) {
        writeImpersonatedUser(buildFakeUserFromDemo(active, state.realUser?.token));
    }
    return true;
}

function getAdminToken(state) {
    return state?.adminToken || state?.realUser?.token || null;
}

async function fetchDemoRoster(adminToken, demoEnvId = null) {
    const url = demoEnvId
        ? `${API_BASE_URL}/api/admin/demo-environments/${demoEnvId}?roster=1`
        : `${API_BASE_URL}/api/admin/demo-users`;
    const res = await fetch(url, {
        headers: {
            Authorization: `Bearer ${adminToken}`,
            Accept: 'application/json',
        },
    });
    if (!res.ok) {
        const err = new Error(`Demo roster fetch failed (${res.status})`);
        err.status = res.status;
        throw err;
    }
    const json = await res.json();
    return demoEnvId ? (json.roster || {}) : json;
}

function applyDemoRosterRefresh(freshDemoUsers) {
    const before = getDemoState();
    if (!before) return { changed: false, accountSwitched: false };

    const beforeId = String(before?.demoUsers?.[before?.selectedRole]?._id || '');
    const tokensUpdated = refreshDemoTokensFromRoster(freshDemoUsers);
    const changed = refreshDemoAccountOptions(freshDemoUsers);
    if (!changed && !tokensUpdated) return { changed: false, accountSwitched: false };

    const after = getDemoState();
    const afterId = String(after?.demoUsers?.[after?.selectedRole]?._id || '');
    return { changed: true, accountSwitched: Boolean(beforeId && beforeId !== afterId) };
}

// `topOffset` lets the parent decide where the bar sits - dashboard layouts
// have a 56px split topbar above it, public layouts don't, so the bar should
// hug the top of the viewport on public routes (the public navbar is pushed
// down by demo-active body padding instead). Defaults to 56 to preserve the
// existing dashboard-layout behaviour for callers that don't specify.
const DemoModeBar = ({ topOffset = 56 } = {}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [demoState, setDemoState] = useState(() => getDemoState());
    const [rosterSyncing, setRosterSyncing] = useState(false);
    const [rosterError, setRosterError] = useState(null);

    useEffect(() => { setDemoState(getDemoState()); }, [location.pathname]);

    const hardNavigate = useCallback((route) => {
        const sep = route.includes('?') ? '&' : '?';
        window.location.href = `${route}${sep}r=${Date.now()}`;
    }, []);

    const syncDemoRoster = useCallback(async () => {
        const state = getDemoState();
        const adminToken = getAdminToken(state);
        if (!adminToken) {
            setRosterError('Re-launch demo from Admin → Demo Dashboards');
            return;
        }

        setRosterSyncing(true);
        setRosterError(null);
        try {
            const fresh = await fetchDemoRoster(adminToken, state.demoEnvId);
            const { changed, accountSwitched } = applyDemoRosterRefresh(fresh);
            if (accountSwitched) {
                const after = getDemoState();
                const slot = after?.demoUsers?.[after?.selectedRole];
                const route = homePathForUser(slot) || ROLE_HOME_ROUTE[after?.selectedRole] || '/dashboard';
                hardNavigate(route);
                return;
            }
            if (changed) setDemoState(getDemoState());
        } catch (err) {
            setRosterError(err.status === 403 ? 'Re-launch demo from Admin → Demo Dashboards' : 'Could not refresh accounts');
        } finally {
            setRosterSyncing(false);
        }
    }, [hardNavigate]);

    // Auto-sync stale account lists on mount (e.g. after Mzansi was added).
    useEffect(() => {
        syncDemoRoster();
    }, [syncDemoRoster]);

    const handleRoleChange = useCallback((e) => {
        const roleKey = e.target.value;
        const route = switchDemoRole(roleKey);
        if (route) {
            setDemoState(getDemoState());
            hardNavigate(route);
        }
    }, [hardNavigate]);

    const handleAccountChange = useCallback((e) => {
        const accountId = e.target.value;
        const state = getDemoState();
        if (!state) return;
        const route = switchDemoAccount(state.selectedRole, accountId);
        if (route) {
            setDemoState(getDemoState());
            hardNavigate(route);
        }
    }, [hardNavigate]);

    const handleBack = useCallback(() => {
        exitDemoMode();
        navigate('/admin');
    }, [navigate]);

    if (!demoState) return null;

    const currentSlot = demoState.demoUsers?.[demoState.selectedRole];
    const currentUser = currentSlot;
    const accountOptions = (Array.isArray(currentSlot?._options) ? currentSlot._options : [])
        .filter((o) => !o.mergedIntoAgencyId);
    const sortedRoles = ROLE_ORDER.filter((k) => (demoState.availableRoles || []).includes(k));

    // For an Agency / Enterprise demo, the User record's primary identifier is
    // the franchise/brand name in `agencyName` (e.g. "Marder Properties"), so we
    // surface that. For everyone else (agents, buyers, sellers, investors,
    // partners) the User record's `agencyName` is just *which agency they work
    // for*, not who they are - so we want the human's `name`. Mixing these up
    // is what caused the Agent dropdown to read agency names instead of the
    // actual agent names.
    const ENTITY_ROLES = new Set(['agency', 'enterprise', 'partner_enterprise', 'partner_bond']);
    const accountLabel = (opt) => {
        if (!opt) return '';
        const role = String(demoState?.selectedRole || '').toLowerCase();
        if (ENTITY_ROLES.has(role)) {
            return opt.agencyName || opt.partnerProfile?.companyName || opt.name || opt.email || 'Demo user';
        }
        const personal = opt.name || opt.email || 'Demo user';
        return opt.agencyName && opt.agencyName !== personal
            ? `${personal} - ${opt.agencyName}`
            : personal;
    };

    // Two distinct accounts can share the same brand name (e.g. two "Tyson
    // Properties" branches). When the base label collides, append a
    // distinguisher so each option is uniquely identifiable in the dropdown.
    const disambiguator = (opt) => {
        const region = opt.regionName || '';
        const branch = opt.branchName && opt.branchName !== opt.agencyName ? opt.branchName : '';
        const loc = String(opt.location || '').split(',')[0].trim();
        return region || branch || loc || opt.email || `#${String(opt._id || '').slice(-4)}`;
    };
    const baseLabelCounts = accountOptions.reduce((m, o) => {
        const l = accountLabel(o);
        m[l] = (m[l] || 0) + 1;
        return m;
    }, {});
    const displayLabel = (opt) => {
        const base = accountLabel(opt);
        if (baseLabelCounts[base] > 1) {
            const extra = disambiguator(opt);
            return extra ? `${base} - ${extra}` : base;
        }
        return base;
    };

    const handleSelectChange = (e) => {
        const val = e.target.value;
        if (val === '__admin__') {
            handleBack();
            return;
        }
        handleRoleChange(e);
    };

    return (
        <div
            className="demo-mode-bar"
            style={{
            position: 'fixed', top: `calc(var(--ipm-preview-banner-h, 0px) + ${topOffset}px)`, left: 0, right: 0, zIndex: 900,
            background: 'var(--brand-chrome, #0d4a4e)', padding: '8px 28px', display: 'flex',
            alignItems: 'center', gap: 14, fontFamily: "var(--brand-font, 'Poppins', sans-serif)",
            boxShadow: '0 2px 12px rgba(0,0,0,0.3)', borderBottom: '2px solid var(--brand-accent, #ffc803)',
        }}>
            <span className="demo-mode-bar__label" style={{ color: 'var(--brand-accent, #ffc803)', fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', flexShrink: 0 }}>
                DEMO MODE
            </span>

            <span style={{ color: 'var(--brand-muted, rgba(255,255,255,0.5))', fontSize: 12, flexShrink: 0 }}>Switch dashboard:</span>
            <select
                value={demoState.selectedRole}
                onChange={handleSelectChange}
                style={{
                    background: '#fff', color: BRAND, border: 'none', borderRadius: 6,
                    padding: '5px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    minWidth: 180,
                }}
            >
                <option value="__admin__" style={{ color: '#64748b', fontWeight: 700 }}>--- Admin Dashboard ---</option>
                {sortedRoles.map((k) => (
                    <option key={k} value={k}>{ROLE_LABELS[k] || k}</option>
                ))}
            </select>

            {accountOptions.length > 1 && (
                <>
                    <span style={{ color: 'var(--brand-muted, rgba(255,255,255,0.5))', fontSize: 12, flexShrink: 0 }}>Account:</span>
                    <select
                        value={String(currentUser?._id || '')}
                        onChange={handleAccountChange}
                        style={{
                            background: '#fff', color: BRAND, border: 'none', borderRadius: 6,
                            padding: '5px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            minWidth: 200, maxWidth: 260,
                        }}
                    >
                        {accountOptions.map((opt) => (
                            <option key={opt._id} value={String(opt._id)}>
                                {displayLabel(opt)}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={syncDemoRoster}
                        disabled={rosterSyncing}
                        title="Refresh demo account list"
                        style={{
                            background: 'color-mix(in srgb, var(--brand-on-chrome, #fff) 14%, transparent)',
                            color: 'var(--brand-on-chrome, #fff)',
                            border: '1px solid color-mix(in srgb, var(--brand-on-chrome, #fff) 25%, transparent)',
                            borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600,
                            cursor: rosterSyncing ? 'wait' : 'pointer', flexShrink: 0,
                        }}
                    >
                        {rosterSyncing ? '…' : '↻'}
                    </button>
                </>
            )}

            {rosterError && (
                <span style={{ color: 'var(--brand-accent, #ffc803)', fontSize: 11, flexShrink: 0 }}>{rosterError}</span>
            )}

            {currentUser && accountOptions.length <= 1 && (
                <span style={{ color: 'var(--brand-muted, rgba(255,255,255,0.5))', fontSize: 11, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    Viewing as: <strong style={{ color: 'var(--brand-on-chrome, rgba(255,255,255,0.7))' }}>{accountLabel(currentUser)}</strong>
                </span>
            )}

            <div style={{ flex: 1 }} />

            <button
                type="button"
                onClick={handleBack}
                style={{
                    background: 'var(--brand-cta, #ffc803)', color: '#fff', border: 'none', borderRadius: 999,
                    padding: '5px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    flexShrink: 0,
                }}
            >
                Exit Demo
            </button>
        </div>
    );
};

export default DemoModeBar;
